import React, { useState, useRef, useEffect, useMemo } from "react";

interface Option {
  _id: string;
  CategoryName?: string;
  otherName?: string;
}

interface DropdownSelectProps {
  value: string | number;
  title?: string;
  name: string;
  options?: Option[]; // ✅ can be undefined
  onChange: (value: string) => void;
  titleofSelect?: string;
  mandatory?: boolean;
  defultSelect?: boolean;
  className?: string;
  disabled?: boolean;
}

const CustomDropdownSelect: React.FC<DropdownSelectProps> = ({
  value,
  title,
  name,
  options = [], // ✅ default safe
  onChange,
  titleofSelect,
  mandatory = false,
  defultSelect = false,
  className,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isInitRef = useRef(true);

  // ✅ Normalize API data (very important)
  const safeOptions = useMemo(
    () =>
      (options || []).map((opt) => ({
        _id: opt?._id ?? "",
        CategoryName: opt?.CategoryName ?? "",
        otherName: opt?.otherName ?? "",
      })),
    [options]
  );

  // ✅ Safe truncate
  const truncateText = (text?: string, maxLength: number = 18) => {
    if (!text) return "";
    return text.length <= maxLength
      ? text
      : text.substring(0, maxLength) + "...";
  };

  // ✅ Default select logic
  useEffect(() => {
    if (!defultSelect) return;

    if (isInitRef.current && safeOptions.length > 0) {
      isInitRef.current = false;

      const exists = safeOptions.some(
        (opt) => opt._id === String(value)
      );

      if (!exists) {
        onChange(safeOptions[0]._id);
      }
    }
  }, [safeOptions, defultSelect, value, onChange]);

  // ✅ Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Safe find
  const selectedOption = safeOptions.find(
    (opt) => opt._id === String(value)
  );

  return (
    <div
      className={`mb-3 ${className} ${disabled ? "opacity-60 cursor-not-allowed w-full" : ""
        }`}
      ref={dropdownRef}
    >
      {title && (
        <div className='flex'>
          <p className='text-sm font-medium mb-1'>{title}</p>
          {mandatory && <p className='text-red-600'>*</p>}
        </div>
      )}

      <div className='relative w-full'>
        <div
          className={`w-full border border-gray-300 rounded px-2 py-2 text-sm cursor-pointer
          ${disabled ? "bg-gray-100 text-gray-500" : "bg-white"}
        `}
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
        >
          <div className='flex justify-between items-center'>
            <div className='flex flex-col'>
              {selectedOption ? (
                <span title={selectedOption.CategoryName}>
                  {truncateText(selectedOption.CategoryName)}
                </span>
              ) : (
                <span className='text-gray-400'>
                  {titleofSelect || `Select ${title}`}
                </span>
              )}
            </div>
            <span>▼</span>
          </div>
        </div>

        {/* ✅ Dropdown list */}
        {isOpen && !disabled && (
          <div className='absolute z-10 w-full bg-white border border-gray-300 rounded shadow-lg max-h-[300px] overflow-y-auto'>
            {/* Default option */}
            {titleofSelect && (
              <div
                className={`px-2 py-2 cursor-pointer hover:bg-gray-100 ${value === "" ? "bg-blue-50 text-blue-600" : ""
                  }`}
                onClick={() => {
                  onChange("");
                  setIsOpen(false);
                }}
              >
                <span className='text-gray-500'>{titleofSelect}</span>
              </div>
            )}

            {/* Options */}
            {safeOptions.length > 0 ? (
              safeOptions.map((opt) => (
                <div
                  key={opt._id}
                  className={`px-2 py-2 cursor-pointer hover:bg-gray-100 ${opt._id === String(value)
                    ? "bg-blue-50 text-blue-600"
                    : ""
                    }`}
                  onClick={() => {
                    onChange(opt._id);
                    setIsOpen(false);
                  }}
                  title={opt.CategoryName}
                >
                  <div className='flex flex-col'>
                    <span>{opt.CategoryName}</span>
                    {opt.otherName && (
                      <p className='text-sm text-slate-500'>
                        {opt.otherName}
                      </p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className='px-2 py-2 text-gray-400 text-sm'>
                No options available
              </div>
            )}
          </div>
        )}
      </div>

      {/* ✅ Hidden select (safe) */}
      <select
        name={name}
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        className='hidden'
        disabled={disabled}
      >
        {safeOptions.map((opt) => (
          <option key={opt._id} value={opt._id}>
            {opt.CategoryName}
          </option>
        ))}
      </select>
    </div>
  );
};

export default CustomDropdownSelect;